/**
 * 30秒ポーリングで /api/locations を取得するフック。
 *
 * - タブが非表示（document.hidden）の間はポーリングを停止し、再表示で再開する（省電力・コスト減）。
 * - アンマウント時は進行中の fetch を AbortController でキャンセルする。
 *
 * 要件 1-6（30秒間隔）/ 1-7（非表示で停止）/ 1-8（再表示で再開）に対応。
 */
import { useEffect, useRef, useState } from 'react';
import { fetchLocations, type LocationsResponse } from '../api/client';

const POLL_INTERVAL_MS = 30_000;

export interface UsePinsResult {
  data: LocationsResponse | null;
  error: Error | null;
  loading: boolean;
}

export function usePins(): UsePinsResult {
  const [data, setData] = useState<LocationsResponse | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    let disposed = false;

    const load = async () => {
      // 進行中のリクエストがあれば中断
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const res = await fetchLocations(controller.signal);
        if (!disposed) {
          setData(res);
          setError(null);
        }
      } catch (err) {
        // 中断は無視。それ以外はエラーとして保持
        if (!disposed && (err as Error).name !== 'AbortError') {
          setError(err as Error);
        }
      } finally {
        if (!disposed) {
          setLoading(false);
        }
      }
    };

    const startPolling = () => {
      if (timerRef.current !== null) {
        return;
      }
      // 開始直後に1回取得してから間隔ポーリング
      void load();
      timerRef.current = setInterval(() => void load(), POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (timerRef.current !== null) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      abortRef.current?.abort();
    };

    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        startPolling();
      }
    };

    // 初期状態に応じて開始（非表示で開かれた場合は開始しない）
    if (!document.hidden) {
      startPolling();
    }
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', handleVisibility);
      stopPolling();
    };
  }, []);

  return { data, error, loading };
}
